import { RadioGroup } from '@headlessui/react'
import DropdownComponent from 'components/DropdownSelectors'
import { DropPosition } from 'components/DropdownSelectors/common'
import MoneyInput from 'components/MoneyInput'
import { useEffect, useState } from 'react'
import { HexColorInput, HexColorPicker } from 'react-colorful'
import { classNames } from 'utils/dom-helpers'
import { FormErrors } from './types'

import 'react-dropzone-uploader/dist/styles.css'
import ImageFileUploader from 'components/FileUploader/Image'
import Translation from 'components/Translation'
import OptionList, {
    ListFlyFrom,
    OptionListItem,
} from 'components/OptionList'
import { ServiceDiscountType, ServiceDiscountTypes, ServiceDisplay, ServiceMemberPriceType, ServiceType } from 'types/service'
import { handleInputEnter } from 'utils/input-helper'
import { useAuth } from 'context/AuthContext'
import { useAppContext } from 'context/AppContext'
import { useTenantContext } from 'context/TenantContext'
import { postApiRequest } from 'utils/fetch-helper'
import { Switch } from '@headlessui/react'
import StepperInput from 'components/Stepper'
import { getTranslation } from 'utils/language-helper'
import ServiceDiscountTypeSelector from 'components/DropdownSelectors/ServiceDiscountTypeSelector'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { Tooltip } from '@progress/kendo-react-tooltip'
import React from 'react'
import { WORD_SINGLE_LINE_REGEX } from 'types/common'
import { ImageIcon, InfoIcon } from 'utils/custom-icons'

function GeneralForm({
    onSubmit,
}: {
    onSubmit(): void
}) {
    const { ServiceModal } = useAuth()
    const { setGlobalError } = useAppErrorApiContext()
    const { translations } = useAppContext()
    const { tenant, service_categories, getServiceCategories } = useTenantContext()
    const { setAttributes, attributes } = ServiceModal

    const [is_category_opened, toggleCategoryOpen] =
        useState<boolean>(false)
    const form_errors: FormErrors =
        (attributes && (attributes.form_errors as unknown as FormErrors)) ||
        {}

    const [is_adding_category, toggleCategoryForm] =
        useState<boolean>(false)
    const [category_name, setServiceCategory] = useState<string>('')
    const [is_dirty, setIsDirty] = useState(false)
    const [service_image, setServiceImage] = useState('')
    const [show_upload_view, setShowUploadView] = useState(true)
    const [show_prevent_duplicate, setShowPreventDuplicate] = useState(false)
    const pricing_tooltip_ref = React.useRef<Tooltip>(null)
    const duplicates_tooltip_ref = React.useRef<Tooltip>(null)

    function handleCategoryChange({
        value: id,
        text: name,
    }: OptionListItem) {
        setIsDirty(true)
        toggleCategoryOpen(false)
        if (id) {
            setAttributes({
                ...attributes,
                category: id,
            })
        }
    }

    function handleNewCategory() {
        toggleCategoryForm(true)
    }

    async function submitNewCategory() {
        if (!WORD_SINGLE_LINE_REGEX.test(category_name)) {
            setGlobalError({ message: "Invalid Category Name" })
            return
        }

        const category = {
            name: category_name.replace(/\s+/g, ' ').trim(),
            type: 'SERVICE',
            tenantId: tenant?.id as string,
        }

        const created_category = await postApiRequest(
            '/v1/categories',
            category
        )

        if (created_category.id) {
            const categories = (attributes?.categories || []) as Record<
                string,
                string
            >[]
            categories.push({
                value: created_category.id,
                text: created_category.name,
            })
            setAttributes({
                ...attributes,
                category: created_category.id,
                categories,
                refetch: true,
            })
            getServiceCategories()
        } else {
            setGlobalError({ message: created_category?.message as string })
        }
        toggleCategoryOpen(false)
        toggleCategoryForm(false)
    }

    useEffect(() => {
        if (!attributes?.price) setAttributes({ ...attributes, price: 0 })
        if (ServiceModal.is_open) {
            setShowPreventDuplicate(attributes?.prevent_duplicate_booking_by_email as boolean 
                || attributes?.prevent_duplicate_booking_by_phone as boolean)
        }
    }, [ServiceModal.is_open])

    useEffect(() => {
        if (attributes?.image_url && !service_image) {
            setServiceImage(attributes?.image_url as string)
            setShowUploadView(false)
        }
    }, [attributes])

    function UrlPreviewComponent(props: any) {
        return (
            <div className="flex flex-col">
                <div className="w-full m-auto">
                    <img
                        src={props.image_url}
                        className="h-32 rounded-lg"
                        alt={props.image_alt}
                    />
                </div>
                <div className="w-full m-auto">
                    <p className="text-sm my-2 w-full">{props.image_alt}</p>
                    <div className="flex w-full">
                        <button
                            onClick={() => setShowUploadView(true)}
                            className="flex w-full gap-3 items-center bg-primary-light text-white px-2 py-1 rounded-lg text-lg justify-center"
                        >
                            <ImageIcon color='white'/>
                            <span>{translations['replace_photo']}</span>
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    function showUploadView() {
        if (!show_upload_view) {
            return (
                <UrlPreviewComponent
                    image_url={service_image as string}
                    image_alt={translations['booking_background_img']}
                />
            )
        } else {
            return (
                <ImageFileUploader
                    tenant_id={tenant?.id}
                    accept=".jpeg, .jpg, .gif, .png"
                    onAdd={handleOnAdd}
                    form_context={{
                        attributes: attributes || {},
                        setAttributes: setAttributes as unknown as (
                            data: Record<string, string | File>
                        ) => void,
                    }}
                    addClassNames={{
                        dropzone:
                            'bg-transparent p-0 overflow-hidden h-64 border-0',
                        inputLabel:
                            'text-gray-400 font-medium text-lg flex-grow',
                        submitButtonContainer: 'mt-3',
                        submitButton:
                            'flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg w-full text-lg font-serif font-light justify-center',
                    }}
                />
            )
        }
    }

    function handleOnAdd(image_url: string) {
        setServiceImage(image_url)
        setShowUploadView(false)
        setIsDirty(true)
    }

    function MembershipPriceSwitch() {
        return (
            <>
                <div className='flex flex-row'>
                    <Switch
                        name="member_price"
                        id="member_price"
                        checked={attributes?.has_member_price as boolean}
                        onChange={() => {
                            setIsDirty(true)
                            setAttributes({ ...attributes, has_member_price: !attributes?.has_member_price, member_price_type: ServiceDiscountTypes[0] })
                        }}
                        className={`${attributes?.has_member_price as boolean
                            ? 'bg-primary-light'
                            : 'bg-gray-300'
                            }
                            block relative inline-flex h-5 w-10 
                            border-2 border-transparent rounded-full cursor-pointer 
                            transition-colors ease-in-out duration-200 
                            focus:outline-none focus-visible:ring-2 
                            focus-visible:ring-white focus-visible:ring-opacity-75`}
                    >
                        <Translation
                            className="sr-only"
                            content_key="services.apply_member_pricing"
                            render_as="span"
                        />
                        <span
                            aria-hidden="true"
                            className={`${attributes?.has_member_price as boolean
                                ? 'translate-x-5'
                                : 'translate-x-0'
                                }
                                pointer-events-none inline-block h-4 w-4 
                                rounded-full bg-white shadow-lg transform ring-0 
                                transition ease-in-out duration-200`}
                        />
                    </Switch>
                    <Translation
                        render_as='label'
                        content_key='services.apply_member_pricing'
                        className='my-auto ml-[14px]'
                    />
                </div>
                {attributes?.has_member_price &&
                    <div className='bg-[#E6E7E8]/30 w-full p-5 mt-3 rounded-lg flex flex-col'>
                        <div className='flex flex-col mb-2'>
                            <div className='flex items-center gap-2'>
                                <Translation
                                    render_as='span'
                                    content_key='services.member_pricing'
                                    className='font-medium text-base text-primary-light'
                                />
                                <span
                                                            title={getTranslation(
                                                                "services.member_pricing_tooltip",
                                                                translations
                                                            )}
                                                        >
                                                            <InfoIcon />
                                                        </span>
                                {/* <span className='text-primary-light text-xs mb-auto'
                                    title={getTranslation("services.member_pricing_tooltip", translations)}
                                    onMouseOver={event => pricing_tooltip_ref.current?.handleMouseOver(event)}
                                    onMouseOut={event => pricing_tooltip_ref.current?.handleMouseOut(event)}
                                >
                                    <i className="feather-info mr-1" />
                                    <Translation
                                        content_key="services.whats_this"
                                    />
                                </span>
                                <Tooltip ref={pricing_tooltip_ref} anchorElement="target" position="top"
                                    content={props =>
                                        <div className='w-60 whitespace-normal'>
                                            <Translation
                                                content_key='services.member_pricing_tooltip'
                                                render_as='span'
                                            />
                                        </div>
                                    }
                                /> */}
                            </div>
                            <div className='flex'>
                                <Translation
                                    content_key='services.select_amount_to_be_redeemed'
                                />
                                <p className='text-secondary'>&nbsp;*</p>
                            </div>
                        </div>
                        <div className='flex flex-row gap-x-4'>
                            <div className='w-1/4'>
                                <ServiceDiscountTypeSelector
                                    selected_item={attributes?.member_price_type as ServiceDiscountType || ServiceDiscountTypes[0]}
                                    setSelectedItem={(selected: ServiceDiscountType) => {
                                        setIsDirty(true)
                                        setAttributes({
                                            ...attributes,
                                            member_price_type: selected
                                        })
                                    }}
                                />
                            </div>
                            <div className='w-1/4'>
                                <input
                                    type="number"
                                    className='w-full rounded-lg'
                                    style={{
                                        border: "1px solid #DDE2E5"
                                    }}
                                    value={attributes?.member_price as number}
                                    onChange={(i) => {
                                        setIsDirty(true)
                                        setAttributes({
                                            ...attributes,
                                            member_price: i.target.value
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                }
            </>
        )
    }

    function PreventDuplicateBookingSwitch() {
        return (
            <>
                <div className="flex flex-row mt-3">
                    <Switch
                        name="prevent_duplicates"
                        id="prevent_duplicates"
                        checked={show_prevent_duplicate}
                        onChange={() => {
                            if (show_prevent_duplicate) {
                                setIsDirty(true)
                                setAttributes({
                                    ...attributes,
                                    prevent_duplicate_booking_by_email:
                                        false,
                                    prevent_duplicate_booking_by_phone:
                                        false,
                                })
                            }
                            setShowPreventDuplicate(!show_prevent_duplicate)
                        }}
                        className={`${
                            show_prevent_duplicate
                                ? 'bg-primary-light'
                                : 'bg-gray-300'
                        }
                            block relative inline-flex h-5 w-10 
                            border-2 border-transparent rounded-full cursor-pointer 
                            transition-colors ease-in-out duration-200 
                            focus:outline-none focus-visible:ring-2 
                            focus-visible:ring-white focus-visible:ring-opacity-75`}
                    >
                        <Translation
                            className="sr-only"
                            content_key="services.apply_prevent_duplicates"
                            render_as="span"
                        />
                        <span
                            aria-hidden="true"
                            className={`${
                                show_prevent_duplicate
                                    ? 'translate-x-5'
                                    : 'translate-x-0'
                            }
                                pointer-events-none inline-block h-4 w-4 
                                rounded-full bg-white shadow-lg transform ring-0 
                                transition ease-in-out duration-200`}
                        />
                    </Switch>
                    <Translation
                        render_as="label"
                        content_key="services.apply_prevent_duplicates"
                        className="my-auto ml-[14px]"
                    />
                </div>
                {show_prevent_duplicate && (
                    <div className="bg-[#E6E7E8]/30 w-full p-5 mt-3 rounded-lg flex flex-col">
                        <div className="flex flex-col mb-2">
                            <div className="flex items-center gap-2">
                                <Translation
                                    render_as="span"
                                    content_key="services.prevent_duplicates"
                                    className="font-medium text-base text-primary-light"
                                />
                                <span
                                    title={getTranslation(
                                        'services.prevent_duplicates_tooltip',
                                        translations
                                    )}
                                >
                                    <InfoIcon />
                                </span>
                                {/* <span
                                    className="text-primary-light text-xs mb-auto"
                                    title={getTranslation(
                                        'services.prevent_duplicates_tooltip',
                                        translations
                                    )}
                                    onMouseOver={(event) =>
                                        duplicates_tooltip_ref.current?.handleMouseOver(
                                            event
                                        )
                                    }
                                    onMouseOut={(event) =>
                                        duplicates_tooltip_ref.current?.handleMouseOut(
                                            event
                                        )
                                    }
                                >
                                    <i className="feather-info mr-1" />
                                    <Translation content_key="services.whats_this" />
                                </span>
                                <Tooltip
                                    ref={duplicates_tooltip_ref}
                                    anchorElement="target"
                                    position="top"
                                    content={(props) => (
                                        <div className="w-60 whitespace-normal">
                                            <Translation
                                                content_key="services.prevent_duplicates_tooltip"
                                                render_as="span"
                                            />
                                        </div>
                                    )}
                                /> */}
                            </div>
                            <div className="flex">
                                <Translation content_key="services.prevent_duplicates_desc" />
                                <p className="text-secondary">&nbsp;*</p>
                            </div>
                        </div>
                        <div className="flex flex-row gap-x-4">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={
                                        attributes?.prevent_duplicate_booking_by_email as boolean
                                    }
                                    className="h-5 w-5  border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light"
                                    onChange={() => {
                                        setAttributes({
                                            ...attributes,
                                            prevent_duplicate_booking_by_email:
                                                !attributes?.prevent_duplicate_booking_by_email,
                                        })
                                    }}
                                />
                                <span className="ml-2 text-sm">
                                    <Translation content_key="services.prevent_by_email_address" />
                                </span>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={
                                        attributes?.prevent_duplicate_booking_by_phone as boolean
                                    }
                                    className="h-5 w-5  border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light"
                                    onChange={() => {
                                        setAttributes({
                                            ...attributes,
                                            prevent_duplicate_booking_by_phone:
                                                !attributes?.prevent_duplicate_booking_by_phone,
                                        })
                                    }}
                                />
                                <span className="ml-2 text-sm">
                                    <Translation content_key="services.prevent_by_phone_number" />
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </>
        )
    }

    function BlockOffTimeSwitch() {
        return (
            <>
                <div className='flex flex-row mt-3'>
                    <Switch
                        name="block_off_time"
                        id="block_off_time"
                        checked={attributes?.show_block as boolean}
                        onChange={() => {
                            setIsDirty(true)
                            setAttributes({ ...attributes, show_block: !attributes?.show_block })
                        }}
                        className={`${attributes?.show_block as boolean
                            ? 'bg-primary-light'
                            : 'bg-gray-300'
                            }
                            block relative inline-flex h-5 w-10 
                            border-2 border-transparent rounded-full cursor-pointer 
                            transition-colors ease-in-out duration-200 
                            focus:outline-none focus-visible:ring-2 
                            focus-visible:ring-white focus-visible:ring-opacity-75`}
                    >
                        <Translation
                            className="sr-only"
                            content_key="services.block_off_time"
                            render_as="span"
                        />
                        <span
                            aria-hidden="true"
                            className={`${attributes?.show_block as boolean
                                ? 'translate-x-5'
                                : 'translate-x-0'
                                }
                                pointer-events-none inline-block h-4 w-4 
                                rounded-full bg-white shadow-lg transform ring-0 
                                transition ease-in-out duration-200`}
                        />
                    </Switch>
                    <Translation
                        render_as='label'
                        content_key='services.block_off_time'
                        className='my-auto ml-[14px]'
                    />
                </div>
                {attributes?.show_block &&
                    <div className='bg-[#E6E7E8]/30 w-full p-5 mt-3 rounded-lg flex flex-col'>
                        <div className="flex flex-col mb-2 gap-1">
                        <Translation
                            render_as='span'
                            content_key='services.block_times'
                            className='font-medium text-base text-primary-light'
                        />
                        <Translation
                            render_as='span'
                            content_key='services.set_block_off_time_in_minutes'
                            className='text-sm'
                        />
                        </div>
                        <div className='flex flex-row gap-4'>
                            <div className='w-1/4'>
                                <Translation
                                    render_as='label'
                                    content_key='services.before_service'
                                    className='font-[450] text-base mb-1.5'
                                />
                                <StepperInput
                                    default_value={
                                        attributes?.blocked_from as number
                                    }
                                    min_value={0}
                                    helper_text={getTranslation(
                                        attributes?.blocked_from == 1 ? "services.block_time_minute" : "services.block_time_minutes",
                                        translations
                                    )}
                                    onChange={(v: number) => {
                                        setIsDirty(true)
                                        setAttributes({
                                            ...attributes,
                                            blocked_from: v
                                        })
                                    }}
                                />
                            </div>
                            <div className='w-1/4'>
                                <Translation
                                    render_as='label'
                                    content_key='services.after_service'
                                    className='font-[450] text-base mb-1.5'
                                />
                                <StepperInput
                                    default_value={
                                        attributes?.blocked_to as number
                                    }
                                    min_value={0}
                                    helper_text={getTranslation(
                                        attributes?.blocked_to == 1 ? "services.block_time_minute" : "services.block_time_minutes",
                                        translations
                                    )}
                                    onChange={(v: number) => {
                                        setIsDirty(true)
                                        setAttributes({
                                            ...attributes,
                                            blocked_to: v
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                }
            </>
        )
    }

    return (
        <form method="POST" onSubmit={onSubmit}>
            <div className="mb-5">
                    {/* <div className="text-primary text-xl font-medium font-display">
                        Enter the basic information for your new service
                    </div> */}
                    <div className="flex items-center justify-center">
                        <RadioGroup
                            value={attributes?.service_type}
                            onChange={(v: string) => {
                                setIsDirty(true)
                                setAttributes({
                                    ...attributes,
                                    service_type: v as string,
                                })
                            }}
                        >
                            {/* <RadioGroup.Label
                                className={classNames(
                                    form_errors.service_type
                                        ? 'text-red-700'
                                        : '',
                                    'text-lg block mb-3'
                                )}
                            >
                                <Translation
                                    content_key="service_type_label"
                                    translations={translations}
                                />
                            </RadioGroup.Label> */}
                            <div className="flex gap-6">
                                {Object.keys(ServiceType).map((v, idx) => {
                                    return (
                                        <RadioGroup.Option
                                            key={v}
                                            value={v}
                                            className={''}
                                            disabled={!!attributes?.id}
                                        >
                                            {({ checked, disabled }) => (
                                                <div
                                                    className={classNames(
                                                        'flex items-center gap-2 w-full h-full rounded-lg px-2 py-1',
                                                        checked
                                                            ? 'border-2 border-[#4B95B7]'
                                                            : 'border border-gray-200 ',
                                                        !disabled
                                                            ? 'cursor-pointer'
                                                            : 'bg-gray-200'
                                                    )}
                                                >
                                                    <img
                                                            src={
                                                                checked
                                                                    ? ServiceDisplay[
                                                                          idx
                                                                      ]
                                                                          .selected
                                                                    : ServiceDisplay[
                                                                          idx
                                                                      ]
                                                                          .active
                                                            }
                                                            className={classNames(
                                                                'h-5 w-5',
                                                                checked
                                                                    ? 'stroke-[#4B95B7] fill-[#4B95B7]'
                                                                    : 'stroke-gray-200 fill-gray-200'
                                                            )}
                                                            alt="Image"
                                                        />
                                                    <span
                                                                className={classNames(
                                                                    'text-base font-medium',
                                                                    disabled
                                                                        ? 'text-zinc-400'
                                                                        : 'text-primary'
                                                                )}
                                                            >
                                                                {
                                                                    Object.values(
                                                                        ServiceType
                                                                    )[idx]
                                                                }
                                                                
                                                            </span>
                                                            <span
                                                            title={getTranslation(
                                                                ServiceDisplay[
                                                                    idx
                                                                ]
                                                                    .translation,
                                                                translations
                                                            )}
                                                        >
                                                            <InfoIcon />
                                                        </span>
                                                </div>
                                            )}
                                        </RadioGroup.Option>
                                    )
                                })}
                            </div>
                        </RadioGroup>
                    </div>
            </div>
            <div className="pb-3 lg:flex gap-5">
                <fieldset className="w-1/2">
                    <div className="flex">
                        <label
                            htmlFor="name"
                            className={classNames(
                                'block text-sm mb-2',
                                form_errors.name ? 'text-red-700' : ''
                            )}
                        >
                            <Translation
                                content_key="name_of_service"
                                translations={translations}
                            />
                        </label>
                        <span className="text-red-500 ml-0.5">*</span>
                    </div>
                    <input
                        type="text"
                        id="name"
                        className={classNames(
                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md',
                            form_errors.name
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        onChange={(e) => {
                            setIsDirty(true)
                            setAttributes({
                                ...attributes,
                                name: e.target.value,
                            })
                        }}
                        defaultValue={(attributes?.name as string) || ''}
                        onKeyPressCapture={handleInputEnter(onSubmit)}
                    />
                    {form_errors.name && (
                        <span className="text-sm text-red-700">
                            <Translation
                                content_key={form_errors.name}
                                translations={translations}
                            />
                        </span>
                    )}
                    <div className='flex'>
                        <Translation
                            className={classNames(
                                'flex text-sm mt-3 mb-2',
                                form_errors.category ? 'text-red-700' : ''
                            )}
                            htmlFor="category"
                            content_key="category_label"
                            translations={translations}
                            render_as="label"
                        />
                        <span className="text-red-500 ml-0.5 mt-3">*</span>
                    </div>

                    <OptionList
                        id="category"
                        error={form_errors.category}
                        position={ListFlyFrom.TOP_RIGHT}
                        onChange={handleCategoryChange}
                        onActivate={() => {
                            toggleCategoryOpen(true)
                        }}
                        defaultValue={attributes?.category as string}
                        className="overflow-auto"
                        button_classnames=""
                        options={service_categories.map(
                            ({ id, name }, idx) => {
                                return {
                                    text: name,
                                    value: id,
                                }
                            }
                        )}
                        static={is_category_opened}
                    >
                        {is_adding_category ? (
                            <div className="bg-white text-primary-light flex flex-col gap-2 w-full h-12">
                                <div className="border-t border-t-gray-300 w-11/12 mx-auto" />
                                <div className="text-primary-light flex items-center gap-2 w-full justify-center cursor-default h-full px-5">
                                    <input
                                        type="text"
                                        className={classNames(
                                            'h-full border-gray-300',
                                            'focus:ring-primary-light focus:border-primary-light',
                                            'w-64 shadow-sm rounded-md flex-1'
                                        )}
                                        onChange={(e) => {
                                            setIsDirty(true)
                                            setServiceCategory(
                                                e.target.value
                                            )
                                        }}
                                        onKeyDown={(e) => {
                                            if (
                                                e.code
                                                    .toUpperCase()
                                                    .indexOf('ENTER') >= 0
                                            ) {
                                                e.preventDefault()
                                                submitNewCategory()
                                            }
                                            if (e.key == ' ') {
                                                e.preventDefault()
                                                setServiceCategory(
                                                    (prev: string) => {
                                                        return prev + ' '
                                                    }
                                                )
                                            }
                                        }}
                                        value={category_name}
                                    />
                                    <button
                                        type="button"
                                        className="flex items-center bg-primary gap-1 text-white rounded px-2 h-full"
                                        onClick={submitNewCategory}
                                    >
                                        <i className="feather-plus text-lg" />{' '}
                                        <Translation
                                            content_key="add"
                                            render_as="span"
                                        />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div
                                onClick={handleNewCategory}
                                className="bg-white w-full py-2 flex flex-col"
                            >
                                <div className="border-t border-t-gray-300 w-11/12 mx-auto" />
                                <div className="text-primary-light flex items-center gap-4 w-full justify-center cursor-default hover:bg-gray-50 py-2">
                                    <i className="feather-plus text-lg" />{' '}
                                    <Translation
                                        content_key="add_new_category"
                                        render_as="span"
                                    />
                                </div>
                            </div>
                        )}
                    </OptionList>

                    {form_errors.category && (
                        <Translation
                            render_as="span"
                            className="text-sm text-red-700 block"
                            content_key={form_errors.category}
                            translations={translations}
                        />
                    )}
                    <Translation
                        render_as="label"
                        content_key="common.primary_color"
                        className={classNames(
                            'block text-sm mt-3 mb-2',
                            form_errors.primary_color ? 'text-red-700' : ''
                        )}
                        htmlFor="primary-color"
                    />

                    <div className="flex gap-3 items-center border border-gray-300 px-3 rounded-lg w-full">
                        <i
                            className="w-6 h-6 rounded-full"
                            style={{
                                backgroundColor:
                                    attributes?.primary_color as string,
                            }}
                        />

                        <HexColorInput
                            id="primary-color"
                            className="focus:outline-none font-mono flex-1 py-3"
                            color={attributes?.primary_color as string}
                            onChange={(primary_color) => {
                                setIsDirty(true)
                                setAttributes({
                                    ...attributes,
                                    primary_color,
                                })
                            }}
                        />

                        <DropdownComponent
                            label={
                                <span className="bg-secondary bg-opacity-10 hover:bg-opacity-5 text-secondary text-thin font-display rounded px-3 text-sm py-1">
                                    <i className="feather feather-edit-2 mr-1 z-10" />
                                    Pick color
                                </span>
                            }
                            dropboxClassname="p-1 border border-gray-200 rounded-lg"
                            className="p-0"
                            position={DropPosition.TOP_RIGHT}
                            style={{}}
                            items={[
                                {
                                    input_component: (
                                        <HexColorPicker
                                            onChange={(primary_color) => {
                                                setIsDirty(true)
                                                setAttributes({
                                                    ...attributes,
                                                    primary_color,
                                                })
                                            }}
                                            color={
                                                (attributes &&
                                                    (attributes.primary_color as string)) ||
                                                '#ccccef'
                                            }
                                        />
                                    ),
                                },
                            ]}
                        />
                    </div>
                </fieldset>
                <fieldset className="w-1/2">
                    <Translation
                        translations={translations}
                        content_key="thumbnail_label"
                        render_as="label"
                        className="text-sm block mb-2"
                    />
                    {showUploadView()}
                </fieldset>
            </div>

            <div className="mb-3 lg:flex gap-5">
                <fieldset className="lg:pb-0 w-full">
                    <Translation
                        className="block text-sm mb-2"
                        htmlFor="description"
                        content_key="description_label"
                        translations={translations}
                        render_as="label"
                    />
                    <textarea
                        className={classNames(
                            'placeholder-gray-300',
                            'focus:ring-primary-light focus:border-primary-light',
                            'block w-full shadow-sm border-gray-300 rounded-md px-2 py-1'
                        )}
                        rows={3}
                        placeholder={
                            translations.description_of_service ||
                            'description_of_service'
                        }
                        onChange={(e) => {
                            setIsDirty(true)
                            setAttributes({
                                ...attributes,
                                description: e.target.value,
                            })
                        }}
                        defaultValue={
                            (attributes?.description as string) || ''
                        }
                    />
                </fieldset>
            </div>
            <div className="pb-3 lg:flex gap-5">
                <fieldset className="lg:w-1/3">
                    <div className="flex">
                        <Translation
                            className={classNames(
                                'block text-sm mb-2',
                                form_errors.duration ? 'text-red-700' : ''
                            )}
                            htmlFor="duration"
                            content_key="duration_label"
                            translations={translations}
                            render_as="label"
                        />
                        <span className="text-red-500 ml-0.5">*</span>
                    </div>

                    <input
                        type="number"
                        id="duration"
                        min={1}
                        onKeyPressCapture={handleInputEnter(onSubmit)}
                        className={classNames(
                            'focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md',
                            form_errors.duration
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        style={{
                            border: '1px solid gray',
                        }}
                        pattern="[0-9]+"
                        defaultValue={(attributes?.duration as number) || 0}
                        onChange={(e) => {
                            setIsDirty(true)
                            setAttributes({
                                ...attributes,
                                duration: e.target.value,
                            })
                        }}
                    />

                    {form_errors.duration && (
                        <Translation
                            content_key={form_errors.duration}
                            className="text-sm text-red-700"
                            render_as="span"
                            translations={translations}
                        />
                    )}
                </fieldset>

                <fieldset className="lg:w-1/3">
                    <div className="flex">
                        <Translation
                            className={classNames(
                                'block text-sm mb-2',
                                form_errors.max_participants
                                    ? 'text-red-700'
                                    : ''
                            )}
                            htmlFor="max_participants"
                            content_key="max_participants_label"
                            translations={translations}
                            render_as="label"
                        />
                        <span className="text-red-500 ml-0.5">*</span>
                    </div>
                    <input
                        type="number"
                        id="max_participants"
                        className={classNames(
                            'focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md',
                            form_errors.max_participants
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        style={{
                            border: '1px solid gray',
                        }}
                        defaultValue={
                            (attributes?.max_participants as number) || 0
                        }
                        onChange={(e) => {
                            setIsDirty(true)
                            setAttributes({
                                ...attributes,
                                max_participants: e.target.value,
                            })
                        }}
                        onKeyPressCapture={handleInputEnter(onSubmit)}
                    />

                    {form_errors.max_participants && (
                        <Translation
                            content_key={form_errors.max_participants}
                            className="text-sm text-red-700"
                            render_as="span"
                            translations={translations}
                        />
                    )}
                </fieldset>

                <fieldset className="lg:w-1/3">
                    <Translation
                        className={classNames(
                            'block text-sm mb-2',
                            form_errors.price ? 'text-red-700' : ''
                        )}
                        htmlFor="price"
                        content_key="price_label"
                        translations={translations}
                        render_as="label"
                    />
                    <MoneyInput
                        id="price"
                        className={classNames(
                            'px-5 focus:ring-0 focus:border-transparent block w-full rounded-md',
                            form_errors.price
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        defaultValue={(attributes?.price as number) || 0}
                        onMoneyChange={(v: string) => {
                            setIsDirty(true)
                            setAttributes({
                                ...attributes,
                                price: +v,
                            })
                        }}
                        onKeyPressCapture={handleInputEnter(onSubmit)}
                    />
                    {form_errors.price && (
                        <Translation
                            content_key={form_errors.price}
                            className="text-sm text-red-700"
                            render_as="span"
                            translations={translations}
                        />
                    )}
                </fieldset>
            </div>
            
            <MembershipPriceSwitch />
            <PreventDuplicateBookingSwitch />
            <BlockOffTimeSwitch />
        </form>
    )
}

export default GeneralForm
